import { FormControl, DefaultFormFields, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getCart } from 'api/operations/cartServiceV2';
import { submitInitiateRFQ } from 'api/operations/enrollmentService';
import { getAgencyAccountRep } from 'api/operations/userService';
import { extendObservable, makeAutoObservable, observable, ObservableMap, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { Cart } from 'types/cartServiceV2';
import { FullUser } from 'types/userService';

interface RFQContacts extends DefaultFormFields {
  defaultEmail: FormControl<string>;
  [key: string]: FormControl<string>;
}

export default class SubmitRFQPageStore {
  private _isLoading = false;
  private _groupId = '';
  private _submitRFQForm: FormController<RFQContacts>;
  private _lastContactIndex: number = 0;
  private _additionalContactFields: ObservableMap<string, boolean> = observable.map({});

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get groupId(): string {
    return this._groupId;
  }

  public get submitRFQForm(): FormController<RFQContacts> {
    return this._submitRFQForm;
  }

  public get additionalContactFields(): string[] {
    return Array.from(this._additionalContactFields.keys());
  }

  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(cartId: string): Promise<void> {
    this.setIsLoading(true);

    const requests = [getCart(cartId), getAgencyAccountRep()];
    const results = await Promise.all(requests);

    runInAction(() => {
      this._groupId = (results[0] as Cart).groupId;
      const defaultEmail = (results[1] as FullUser)?.email || '';
      this._submitRFQForm.populateFields({ defaultEmail });

      this.setIsLoading(false);
    });
  }

  public async submitInitiateRFQ(periodId: string, cartId: string): Promise<void> {
    this.setIsLoading(true);
    const { defaultEmail, ...additionalContactFields } = this._submitRFQForm.getFormDataObject();

    await submitInitiateRFQ(periodId, cartId, [defaultEmail, ...Object.values(additionalContactFields)]);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  // Manage additional contacts helpers
  public addContact(): void {
    this._lastContactIndex += 1;
    extendObservable(this._submitRFQForm.fields, {
      [`additionalEmail${this._lastContactIndex}`]: new FormControl<string>('', { validations: ['isEmailValid'] }),
    });
    this._additionalContactFields.set(`additionalEmail${this._lastContactIndex}`, true);
  }

  public removeContact(id: string): void {
    delete this._submitRFQForm.fields[id];
    this._additionalContactFields.delete(id);
  }

  public getContactField(fieldKey: string): FormControl<string> {
    if (!this._submitRFQForm.fields[fieldKey]) {
      throw new Error(`Attempted to access uninitialized field of key: ${fieldKey}`);
    }
    return this._submitRFQForm.fields[fieldKey];
  }

  constructor() {
    this._submitRFQForm = new FormController({
      defaultEmail: new FormControl<string>('', { validations: ['isEmailValid'], trim: true }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new SubmitRFQPageStore());

export const useSubmitRFQPageStore = (): SubmitRFQPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
